import {ChangeDetectionStrategy, Component, computed, effect, inject, signal} from '@angular/core';
import {rxResource} from '@angular/core/rxjs-interop';
import {firstValueFrom} from 'rxjs';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {
  AuditInfo,
  CriteriaWeightingResponse,
  EvaluationCriteriaResponse,
  PairwiseComparisonRequest,
  ProjectRoleType,
  ProjectStepType,
  UpsertCriteriaWeightingRequest,
  WeightingMode,
} from '@frontend/shared-api';
import {EvaluationCriteria, EvaluationSubCriteriaDirectus} from '@frontend/directus';
import {TranslocoPipe} from '@jsverse/transloco';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {ToastService} from '@frontend/toast';
import {ProjectStepBaseReactive} from '../../../project-base-step/project-base-step-reactive.component';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading.component';
import {StepDotMenuComponent} from '../../../../../../shared/components/step-dot-menu/step-dot-menu.component';
import {SubCriteriaItem} from '../../../../../../shared/model/sub-criteria.interfaces';
import {
  isEC3,
  isEC8,
  isEC9,
} from '../../../../../../shared/constants/evaluation-criteria.constants';
import {TabStatusEnum} from '../../../../../../shared/tabs/tab-status.model';
import {TabStatusDirective} from '../../../../../../shared/tabs/tab-status.directive';
import {logTabRender} from '../../../../../../shared/tabs/tab-render-log.util';
import {SubCriteriaTabXComponent} from './sub-criteria-tab/sub-criteria-tab.component';
import {SubCriteriaInclusionSaveX} from './sub-criteria-tab/sub-criteria-tab.types';
import {PairwiseComparisonX} from '../evaluation-criteria-x.types';
import {CriteriaTabX} from './step-4-2-sub-criteria-evaluation-x.types';
import {Step42SubCriteriaEvaluationXService} from './step-4-2-sub-criteria-evaluation-x.service';
import {TabsUnsavedRevealDirective} from '../../../../../../shared/tabs/tabs-unsaved-reveal.directive';

@Component({
  selector: 'app-step-4-2-sub-criteria-evaluation-x',
  imports: [
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    TranslocoPipe,
    TranslateDirectusPipe,
    LoadingComponent,
    StepDotMenuComponent,
    SubCriteriaTabXComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './step-4-2-sub-criteria-evaluation-x.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Step42SubCriteriaEvaluationXComponent extends ProjectStepBaseReactive {
  protected readonly ProjectRoleType = ProjectRoleType;
  protected readonly ProjectStepType = ProjectStepType;

  private readonly service = inject(Step42SubCriteriaEvaluationXService);
  private readonly toast = inject(ToastService);

  protected readonly activeTabIndex = signal(0);
  protected readonly isSaving = signal(false);
  private readonly initialLoadDone = signal(false);

  private readonly criteriaResource = rxResource({
    params: () => this.projectId(),
    stream: ({params: projectId}) => this.service.loadCriteria(projectId),
  });

  private readonly directusCriteriaResource = rxResource({
    stream: () => this.service.loadDirectusCriteria(),
  });

  private readonly directusSubCriteriaResource = rxResource({
    stream: () => this.service.loadDirectusSubCriteria(),
  });

  private readonly comparisonsResource = rxResource({
    params: () => this.projectId(),
    stream: ({params: projectId}) => this.service.loadComparisons(projectId),
  });

  private readonly resourcesLoading = computed<boolean>(
    () =>
      this.isLoadingState() ||
      this.criteriaResource.status() === 'loading' ||
      this.directusCriteriaResource.status() === 'loading' ||
      this.directusSubCriteriaResource.status() === 'loading' ||
      this.comparisonsResource.status() === 'loading',
  );

  protected readonly loading = computed<boolean>(
    () => !this.initialLoadDone() && this.resourcesLoading(),
  );

  private readonly criteria = computed<EvaluationCriteriaResponse[]>(
    () => this.criteriaResource.value() ?? [],
  );

  private readonly directusCriteriaById = computed<Map<string, EvaluationCriteria>>(
    () =>
      new Map(
        (this.directusCriteriaResource.value() ?? []).map((criteria) => [criteria.id, criteria]),
      ),
  );

  private readonly directusSubCriteriaById = computed<Map<string, EvaluationSubCriteriaDirectus>>(
    () =>
      new Map(
        (this.directusSubCriteriaResource.value() ?? []).map((subCriterion) => [
          subCriterion.id,
          subCriterion,
        ]),
      ),
  );

  private readonly subCriteriaByCriteriaId = computed<Record<number, SubCriteriaItem[]>>(() => {
    const directusSubCriteriaById = this.directusSubCriteriaById();
    const subCriteriaByCriteriaId: Record<number, SubCriteriaItem[]> = {};

    for (const criteria of this.criteria()) {
      subCriteriaByCriteriaId[criteria.id] = criteria.subCriteria.map((subCriterion) => ({
        id: subCriterion.id,
        directusId: subCriterion.directusId,
        translations: directusSubCriteriaById.get(subCriterion.directusId)?.translations ?? [],
        included: subCriterion.included,
        scNumber: subCriterion.scNumber,
        isSaving: false,
      }));
    }

    return subCriteriaByCriteriaId;
  });

  protected readonly tabs = computed<CriteriaTabX[]>(() => {
    const directusCriteriaById = this.directusCriteriaById();
    const subCriteriaByCriteriaId = this.subCriteriaByCriteriaId();
    const comparisonsByCriteriaId = this.comparisonsByCriteriaId();

    return this.criteria().map((criteria) => {
      const subCriteria = [...(subCriteriaByCriteriaId[criteria.id] ?? [])].sort(
        (first, second) => first.scNumber - second.scNumber,
      );

      return {
        id: criteria.id,
        ecNumber: criteria.ecNumber,
        directusId: criteria.directusId,
        identifier: `EC${criteria.ecNumber}`,
        translations: directusCriteriaById.get(criteria.directusId)?.translations ?? [],
        subCriteria,
        status: this.resolveTabStatus(
          criteria,
          subCriteria,
          comparisonsByCriteriaId.get(criteria.id) ?? [],
        ),
        hasUnsavedChanges: this.hasDefaultInclusions(criteria.ecNumber, subCriteria),
      };
    });
  });

  protected readonly hasTabs = computed<boolean>(() => this.tabs().length > 0);

  protected readonly subCriteriaLoading = computed<boolean>(() =>
    this.directusSubCriteriaResource.isLoading(),
  );

  private readonly subCriteriaIdsByCriteriaId = computed<Map<number, Set<number>>>(() => {
    const idsByCriteriaId = new Map<number, Set<number>>();

    for (const criteria of this.criteria()) {
      idsByCriteriaId.set(
        criteria.id,
        new Set(criteria.subCriteria.map((subCriterion) => subCriterion.id)),
      );
    }

    return idsByCriteriaId;
  });

  protected readonly comparisonsByCriteriaId = computed<Map<number, PairwiseComparisonX[]>>(() => {
    const idsByCriteriaId = this.subCriteriaIdsByCriteriaId();
    const comparisonsByCriteriaId = new Map<number, PairwiseComparisonX[]>();
    const comparisons = this.comparisonsResource.value()?.comparisons ?? [];

    for (const [criteriaId, subCriteriaIds] of idsByCriteriaId) {
      comparisonsByCriteriaId.set(
        criteriaId,
        comparisons.filter(
          (comparison) =>
            subCriteriaIds.has(comparison.firstId) && subCriteriaIds.has(comparison.secondId),
        ),
      );
    }

    return comparisonsByCriteriaId;
  });

  protected readonly weightingByCriteriaId = computed<Map<number, CriteriaWeightingResponse>>(
    () => {
      const weightingByCriteriaId = new Map<number, CriteriaWeightingResponse>();

      for (const criteria of this.criteria()) {
        weightingByCriteriaId.set(criteria.id, {
          mode: criteria.subCriteriaWeightingMode,
          equalWeights: criteria.subCriteriaEqualWeights,
          weights: criteria.subCriteria.map((subCriterion) => ({
            id: subCriterion.id,
            rankPosition: subCriterion.rankPosition ?? subCriterion.scNumber,
            weight: subCriterion.weight,
          })),
        });
      }

      return weightingByCriteriaId;
    },
  );

  protected readonly auditInfo = computed<AuditInfo | null>(
    () => this.comparisonsResource.value()?.auditInfo ?? null,
  );

  constructor() {
    super();

    effect(() => {
      if (!this.resourcesLoading()) {
        this.initialLoadDone.set(true);
      }
    });
  }

  onTabChange(index: string | number | undefined): void {
    const tabIndex = Number(index ?? 0);
    logTabRender('S', tabIndex);
    this.activeTabIndex.set(tabIndex);
  }

  async onInclusionsSave(save: SubCriteriaInclusionSaveX): Promise<void> {
    this.isSaving.set(true);

    try {
      await firstValueFrom(
        this.service.saveInclusions(this.projectId()!, save.ecNumber, save.inclusions),
      );
      this.criteriaResource.reload();
      this.comparisonsResource.reload();
      await this.stepState.loadStepStatuses(this.projectId()!);
      await this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
    } catch (error) {
      console.error('Error saving sub criteria inclusions:', error);
    } finally {
      this.isSaving.set(false);
    }
  }

  async onComparisonsSave(comparisons: PairwiseComparisonRequest[]): Promise<void> {
    this.isSaving.set(true);

    try {
      await firstValueFrom(this.service.saveComparisons(this.projectId()!, comparisons));
      this.comparisonsResource.reload();
      this.criteriaResource.reload();
      await this.stepState.loadStepStatuses(this.projectId()!);
      await this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
    } catch (error) {
      console.error('Error saving sub criteria comparisons:', error);
    } finally {
      this.isSaving.set(false);
    }
  }

  async onWeightingSave(
    evaluationCriteriaId: number,
    request: UpsertCriteriaWeightingRequest,
  ): Promise<void> {
    this.isSaving.set(true);

    try {
      await firstValueFrom(
        this.service.saveWeighting(this.projectId()!, evaluationCriteriaId, request),
      );
      this.criteriaResource.reload();
      await this.stepState.loadStepStatuses(this.projectId()!);
      await this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
    } catch (error) {
      console.error('Error saving sub criteria weighting:', error);
    } finally {
      this.isSaving.set(false);
    }
  }

  private hasDefaultInclusions(ecNumber: number, subCriteria: SubCriteriaItem[]): boolean {
    if (isEC3(ecNumber) || isEC8(ecNumber) || isEC9(ecNumber)) {
      return false;
    }

    return subCriteria.some((subCriterion) => subCriterion.included == null);
  }

  private resolveTabStatus(
    criteria: EvaluationCriteriaResponse,
    subCriteria: SubCriteriaItem[],
    comparisons: PairwiseComparisonX[],
  ): TabStatusEnum {
    const decidedSubCriteria = subCriteria.filter((subCriterion) => subCriterion.included != null);

    if (decidedSubCriteria.length === 0) {
      return TabStatusEnum.NOT_STARTED;
    }

    const allDecided = decidedSubCriteria.length === subCriteria.length;
    const weightingComplete =
      !this.usesPairwiseComparisons(criteria) || this.isPairwiseComplete(subCriteria, comparisons);

    return allDecided && weightingComplete ? TabStatusEnum.COMPLETED : TabStatusEnum.IN_PROGRESS;
  }

  private usesPairwiseComparisons(criteria: EvaluationCriteriaResponse): boolean {
    return (
      !criteria.subCriteriaEqualWeights &&
      criteria.subCriteriaWeightingMode === WeightingMode.PAIRWISE
    );
  }

  private isPairwiseComplete(
    subCriteria: SubCriteriaItem[],
    comparisons: PairwiseComparisonX[],
  ): boolean {
    const includedSubCriteria = subCriteria.filter(
      (subCriterion) => subCriterion.included === true && subCriterion.id != null,
    );

    if (includedSubCriteria.length < 2) {
      return true;
    }

    const baseItem = includedSubCriteria[0];

    return includedSubCriteria
      .slice(1)
      .every((subCriterion) =>
        comparisons.some(
          (comparison) =>
            (comparison.firstId === baseItem.id && comparison.secondId === subCriterion.id) ||
            (comparison.firstId === subCriterion.id && comparison.secondId === baseItem.id),
        ),
      );
  }
}
